/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { GtoUom } from '@gh/builder-shared-data';
import {
  getPositiveIntegralParams, getRangedDecimalParams,
  getRangedIntegralParams,
  QuantityTypes,
} from '@gh/common-shared';
import { createQuantity, Foot } from '@gh/core-data';
import { numberType, quantityNumberType, quantityPatternType, QuantityType } from '@gh/core-type';

export const BaseQuantityNumberType = quantityNumberType({
  messages: {
    min: 'msg_field_invalid_format',
    max: 'msg_field_invalid_format',
    range: 'msg_field_invalid_range',
  },
});

export const QPositiveSmallint = quantityNumberType(BaseQuantityNumberType, {
  ...getPositiveIntegralParams(15),
});

const Q_2_15 = Math.pow(2, 15);

export const QSmallint = quantityNumberType(BaseQuantityNumberType, {
  ...getRangedIntegralParams({ min: -Q_2_15, max: Q_2_15, includeMin: true, includeMax: true }),
});

const QLength = quantityNumberType(BaseQuantityNumberType, {
  ...getRangedDecimalParams({ precision: 14, scale: 2, includeMin: false, includeMax: false }),
});

export const BuilderQuantityTypes = {
  QCount: quantityNumberType(QLength, {}),
  QMillimeter: quantityNumberType(QLength, { label: 'mm' }),
  QMeter: quantityNumberType(QLength, { label: 'm' }),
  QMeter2: quantityNumberType(QLength, { label: 'm2' }),
  QFoot: quantityPatternType<Foot>({
    format: 'ft',
    types: {
      'ft': numberType(getPositiveIntegralParams(31)),
      'in': numberType(getRangedIntegralParams({ min: 0, max: 11, includeMin: true, includeMax: true })),
    },
    mappings: {
      parse: ({ negative, elements }) => createQuantity(<Foot>{ negative, ...elements, delta: 0 }, GtoUom.Feets),
      format: (q) => q.value ? ({ negative: q.value.negative, elements: { ft: q.value.ft, in: q.value.in } }) : void 0,
    },
  }),
  QFoot2: quantityNumberType(QLength, { label: 'ft2' }),
};

const UOM_QUANTITY_TYPES = {
  [GtoUom.Count]: BuilderQuantityTypes.QCount,
  [GtoUom.Millimeters]: BuilderQuantityTypes.QMillimeter,
  [GtoUom.Meters]: BuilderQuantityTypes.QMeter,
  [GtoUom.MetersSquare]: BuilderQuantityTypes.QMeter2,
  [GtoUom.Feets]: BuilderQuantityTypes.QFoot,
  [GtoUom.FeetsSquare]: BuilderQuantityTypes.QFoot2,
};

export const getUomQuantityType = (uom: GtoUom): QuantityType<any> => UOM_QUANTITY_TYPES[uom] || QuantityTypes.QGeneric;
